// format.js — Indian number formatting, ₹Cr, band helpers (spec §9)

const enIN = new Intl.NumberFormat('en-IN', { maximumFractionDigits: 2, minimumFractionDigits: 2 });
const enINint = new Intl.NumberFormat('en-IN', { maximumFractionDigits: 0 });

export const fmt = (n, d = 2) => {
  if (n === null || n === undefined || Number.isNaN(n)) return '—';
  return new Intl.NumberFormat('en-IN', { minimumFractionDigits: d, maximumFractionDigits: d }).format(n);
};

export const fmtInt = (n) => {
  if (n === null || n === undefined || Number.isNaN(n)) return '—';
  return enINint.format(n);
};

export const fmtCr = (n) => {
  if (n === null || n === undefined || Number.isNaN(n) || n <= 0) return '—';
  const d = n >= 100 ? 0 : 1;
  // NBSP between the number and "Cr" so table cells never break the unit onto a new line
  return `₹${new Intl.NumberFormat('en-IN', { minimumFractionDigits: d, maximumFractionDigits: d }).format(n)}\u00A0Cr`;
};

export const fmtPct = (n) => {
  if (n === null || n === undefined || Number.isNaN(n)) return '—';
  return `${Math.round(n)}%`;
};

// Band rule — spec §4.3
export const band = (s) => {
  if (s === null || s === undefined || Number.isNaN(s)) return null;
  if (s >= 4.6) return 'Excellent';
  if (s >= 3.6) return 'Good';
  if (s >= 2.6) return 'Moderate';
  return 'Poor';
};

export const BAND_COLOR = {
  Excellent: '#1f8a4c',
  Good: '#5aa06a',
  Moderate: '#d98a1f',
  Poor: '#c8452f',
};

export const bandColor = (b) => BAND_COLOR[b] || '#98a1b8';

// Criterion accents (§6)
export const CRIT_COLOR = {
  Relevance: '#04294f',
  Coherence: '#12559a',
  Efficiency: '#2e7cbf',
  Effectiveness: '#6a8fb5',
  Impact: '#e23d28',
  Sustainability: '#9b2d20',
  Branding: '#8a4b3a',
};

// Thematic tool accents (§6)
export const TOOL_COLOR = {
  NRM: '#04294f',
  Health: '#2e7cbf',
  'H&H': '#2e7cbf',
  SDLE: '#e23d28',
  POE: '#9b2d20',
  PoE: '#9b2d20',
};

export const TOOL_LABEL = { NRM: 'NRM', Health: 'H&H', SDLE: 'SDLE', POE: 'PoE' };

export const CRIT_ORDER = ['Relevance', 'Coherence', 'Efficiency', 'Effectiveness', 'Impact', 'Sustainability', 'Branding'];
export const TOOL_ORDER = ['NRM', 'Health', 'SDLE', 'POE'];

// Abbreviated criterion label for tight columns
export const CRIT_ABBR = {
  Relevance: 'Rel',
  Coherence: 'Coh',
  Efficiency: 'Eff',
  Effectiveness: 'Effs',
  Impact: 'Imp',
  Sustainability: 'Sus',
  Branding: 'Brd',
};

// Score chip style (bg = bandColor+"20", text = bandColor)
export const scoreCellStyle = (s) => {
  const b = band(s);
  if (!b) return { background: '#eef1f7', color: '#98a1b8' };
  const c = bandColor(b);
  return { background: `${c}20`, color: c, border: `1px solid ${c}33` };
};

// Mode helper — most-frequent value of an array
export const mode = (arr) => {
  const counts = new Map();
  for (const v of arr) {
    if (v === null || v === undefined || v === '') continue;
    counts.set(v, (counts.get(v) || 0) + 1);
  }
  let best = null, n = -1;
  counts.forEach((c, v) => { if (c > n) { n = c; best = v; } });
  return best;
};

export const mean = (arr) => {
  const clean = arr.filter((v) => v !== null && v !== undefined && !Number.isNaN(v));
  if (clean.length === 0) return null;
  return clean.reduce((a, b) => a + b, 0) / clean.length;
};

export const median = (arr) => {
  const clean = arr.filter((v) => v !== null && v !== undefined && !Number.isNaN(v)).sort((a, b) => a - b);
  if (clean.length === 0) return null;
  const m = Math.floor(clean.length / 2);
  return clean.length % 2 ? clean[m] : (clean[m - 1] + clean[m]) / 2;
};
